/* eslint-disable max-classes-per-file */
import { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import { LC_GLOBAL_JS_TROUBLESHOOT } from '@mm/api-routes';
import { ChatWidgetTroubleshootingController } from '../controllers/ChatWidgetTroubleshootingController';
import {
  ChatWidgetDiagnosticResult,
  ChatWidgetTroubleshootingToolIssueType,
} from '../interfaces/chatWidgetTroubleshooting';
import { PromoterModel } from '../model/PromoterModel';
import { domainSelector } from '../selectors/domainSelector';
import { activeFacebookPageSelector } from '../selectors/facebookPageSelector';
import {
  activeChatbotSelector,
  chatbotIsActiveOnConnectedPageSelector,
} from '../selectors/viewSelectors';
import { StoreState } from '../store-interfaces';

export type ChatWidgetTroubleshootingAction =
  | {
      type: 'CHATWIDGETTROUBLESHOOTING__RUN';
      result: ChatWidgetDiagnosticResult;
    }
  | {
      type: 'CHATWIDGETTROUBLESHOOTING__CLEAR';
    }
  | {
      type: 'CHATWIDGETTROUBLESHOOTING__NO_ISSUES_MOUNT';
    }
  | {
      type: 'CHATWIDGETTROUBLESHOOTING__NO_ISSUES_UNMOUNT';
    };

export function isChatWidgetTroubleshootingError(
  err: any,
): err is ChatWidgetTroubleshootingError {
  return err?.name === 'ChatWidgetTroubleshootingError';
}
class ChatWidgetTroubleshootingError extends Error {
  status: number;
  constructor(status: number) {
    super('Chat Widget Troubleshooting Tool failed');
    this.name = 'ChatWidgetTroubleshootingError';
    this.status = status;
  }
}
class ChatWidgetTroubleshootingActions {
  run(
    url: string,
    fbPageId: string,
    issueType: ChatWidgetTroubleshootingToolIssueType,
    botId: number,
    promoter?: PromoterModel,
  ) {
    return async (
      dispatch: Dispatch<StoreState>,
      getState: () => StoreState,
    ) => {
      const state = getState();
      const domains = domainSelector(state);
      const userAuthToken = state.session.session.authToken;
      const activeFacebookPage = activeFacebookPageSelector(state);
      const botActive = chatbotIsActiveOnConnectedPageSelector(state);
      const { chatWidgetChannel } = activeChatbotSelector(state);

      if ((!activeFacebookPage && !fbPageId) || !promoter?.id) {
        return;
      }
      const getDomainWhitelisted = () => {
        if (
          chatWidgetChannel !== 'facebook' &&
          chatWidgetChannel !== 'omnichat'
        ) {
          return;
        }
        if (!domains.domainsList) {
          return;
        }
        const parsedUrl = new URL(url);
        return !!domains.domainsList.find(
          d => new URL(d).origin === parsedUrl.origin,
        );
      };

      const backendTests = ChatWidgetTroubleshootingController.runTests(
        botId,
        promoter.id,
      );

      const response = await fetch(
        LC_GLOBAL_JS_TROUBLESHOOT(url, fbPageId, chatWidgetChannel),
        {
          method: 'POST',
          headers: {
            'MM-User-Authtoken': userAuthToken,
            'MM-Remote-Page-Id': activeFacebookPage?.remoteId || fbPageId,
          },
        },
      );
      if (response.status !== 200) {
        throw new ChatWidgetTroubleshootingError(response.status);
      }
      let result = {
        url,
        localTests: {
          chatWidgetChannel,
          botActive,
          domainWhitelisted: getDomainWhitelisted(),
          promoterActive: promoter.active,
          fbPageId,
        },
        backendTests: await backendTests,
        ...(await response.json()),
      } as ChatWidgetDiagnosticResult;
      try {
        const res = await ChatWidgetTroubleshootingController.postLog(
          url,
          fbPageId,
          issueType,
          botId,
          promoter.id,
          result,
        );
        result = { ...res, ...result };
      } catch {} // Ignore errors

      dispatch({
        type: 'CHATWIDGETTROUBLESHOOTING__RUN',
        result,
      } as ChatWidgetTroubleshootingAction);

      return result;
    };
  }

  clear() {
    return async (dispatch: Dispatch<StoreState>) => {
      await dispatch({
        type: 'CHATWIDGETTROUBLESHOOTING__CLEAR',
      } as ChatWidgetTroubleshootingAction);
    };
  }
}

export const chatWidgetTroubleshootingActions =
  new ChatWidgetTroubleshootingActions();

export const useChatWidgetTroubleshootingActions = () => {
  const dispatch: Dispatch<StoreState> = useDispatch();

  return useMemo(
    () => ({
      clear: () => dispatch(chatWidgetTroubleshootingActions.clear()),
      run: async (
        ...args: Parameters<ChatWidgetTroubleshootingActions['run']>
      ) => dispatch(chatWidgetTroubleshootingActions.run(...args)),
    }),
    [dispatch],
  );
};
